import * as React from 'react';
import axios from 'axios';
import { IReactStep } from './steps';
import { withJob } from 'react-jobs';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import * as api from '../../data/api/api';
import { bindActionCreators } from 'redux';
import { IStore } from '../../redux/IStore';
import { formValueSelector } from 'redux-form';
import * as Commonmark from 'react-commonmark';
import Loading from '../../components/Loading/index';
import QuoteSidebar from './components/QuoteSidebar';
import { flow, isUndefined, each, get, set } from 'lodash';
import { IAuthState } from '../../services/auth/models';
import { actions as quoteActions } from '../../services/quote/reducer';
import { actions as brandingActions } from '../../services/branding/reducer';
import { Route, RouteComponentProps, Switch, withRouter, Redirect } from 'react-router';
import { Col, Modal as BootstrapModal, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { IQuoteActions, IQuoteState as IQuoteServiceState } from '../../services/quote/models';
import {
  IChannel,
  ICustomer,
  IPolicyApplication,
  IProduct,
  IStep,
  QuoteStatus,
} from '../../data/api/models';
import { IBrandingActions, IBrandingState, II18nMessages } from '../../services/branding/models';
import { Link } from 'react-router-dom';
import FaInfoCircle from 'react-icons/fa/info-circle';
import { getReferralsAndDeclines } from '../../util/helpers';
import { AutoAffix } from 'react-overlays';

const ModalHeader = BootstrapModal.Header;
const ModalTitle = BootstrapModal.Title;
const ModalBody = BootstrapModal.Body;

interface IQuoteProps extends RouteComponentProps<any> {
  auth: IAuthState;
  i18n: II18nMessages;
  steps: IReactStep[];
  channel: IChannel;
  customer: ICustomer;
  encryptCardCvv: string;
  branding: IBrandingState;
  quote: IQuoteServiceState;
  encryptCardNumber: string;
  actions: {
    quote: IQuoteActions;
    branding: IBrandingActions,
    push(route: string);
  };
}

interface IQuoteState {
  showModal: boolean;
  values: any;
}

class Quote extends React.Component<IQuoteProps, IQuoteState> {
  public state = {
    showModal: false,
    values: null,
    transactionId: null,
  };

  public componentWillMount() {
    const { actions } = this.props;
    actions.branding.getProduct();

    if (process.env.BROWSER) {
      window.addEventListener('message', this.handleWindowMessage);
    }
  }

  private handleWindowMessage = (event: MessageEvent) => {
    const { actions } = this.props;

    // For Chrome, the origin property is in the event.originalEvent object.
    const origin = event.origin || event.originalEvent.origin;

    if (origin !== axios.defaults.baseURL) {
      return;
    }

    const data = event.data;

    this.scrollToTop();

    if (data.status !== 'complete' && data.status !== 'confirmed') {
      // Show an error?

      actions.quote.error('Payment Error: ' + data.message);
      this.closeModal();

      return;
    }

    // The payment page only reports the payment, the policy is issued afterwards. The modal stays open (showing the
    // provider's "please wait" page) until that finishes.
    const values = this.state.values;

    return actions.quote.purchase(values)
      .then((purchased) => {
        this.closeModal();
        this.completePurchase(purchased, '/complete');
      })
      .catch((error) => {
        this.closeModal();
        this.handlePurchaseError(values, error);
      });
  }

  private completePurchase = (data, route: string) => {
    const { actions } = this.props;

    actions.quote.complete(data.policies[0]);
    actions.quote.addStepToWizard('complete', false);

    actions.push(route);

    this.scrollToTop();
  }

  // Returns false when the error was the payment provider redirect (the payment modal is opened instead)
  private handlePurchaseError = (formValues, error): boolean => {
    const { actions } = this.props;
    let rethrow = true;
    let message = get(error, 'response') ? null : error.message;

    each(get(error, 'response.data.errors', []), (e: any) => {
      if (e.id !== 'redirect-to-provider') {
        message = e.detail;
        return;
      }

      this.openModal(formValues, e.data.transactionId);
      rethrow = false;
    });

    if (rethrow && message) {
      actions.quote.error(message);
      this.scrollToTop();
    }

    return rethrow;
  }

  public openModal = (values, transactionId) => {
    this.setState({
      showModal: true,
      transactionId,
      values,
    });
  }

  public closeModal = () => {
    this.setState({
      showModal: false,
      values: null,
    });
  }

  private getCurrentLocation = () => {
    const { steps } = this.props;

    return this.getCurrentLocationFromSteps(steps);
  }

  private getCurrentLocationFromSteps = (steps) => {
    const { location } = this.props;
    let currentLocationIndex = null;

    steps.forEach((step, key) => {
      if (step.route === location.pathname) {
        currentLocationIndex = key;
      }
    });

    return currentLocationIndex;
  }

  private scrollToTop = () => {
    if (process.env.BROWSER) {
      window.scrollTo(0, 0);
    }
  }

  private handleBack = () => {
    const { steps, actions } = this.props;

    const currentLocation = this.getCurrentLocation();

    actions.push(steps[ currentLocation - 1 ].route);
    this.scrollToTop();
  }

  private getNextStep = (formValues: IPolicyApplication): IReactStep => {
    const { steps } = this.props;
    const currentLocation = this.getCurrentLocation();

    let nextStep = steps [currentLocation + 1];
    for (let i = currentLocation + 1; i < steps.length; i++) {
      nextStep = steps[i];

      if (! nextStep.hasOwnProperty('enabled')) {
        break;
      }

      if (nextStep.enabled(formValues)) {
        break;
      }
    }

    return nextStep;
  }

  private handleCalculate = () => {
    const { actions } = this.props;

    return actions.quote.submitForm(null, true, true)
      .then((formValues: IPolicyApplication) => {
        actions.quote.calculate(formValues);
      });
  }

  private handleSubmit = (formValues: IPolicyApplication) => {
    const { steps, actions, encryptCardNumber, encryptCardCvv } = this.props;
    const currentLocation = this.getCurrentLocation();
    const currentStep = steps [ currentLocation ];
    const nextStep = this.getNextStep(formValues);
    const isFinalStep = nextStep.key === 'complete';

    if (! isFinalStep) {
      formValues.steps.push(nextStep.key);
      actions.quote.addStepToWizard(nextStep.key);
    }
    const handleNextStep = this.handleNextStep(formValues, nextStep);

    if (isUndefined(currentStep.key) || ! currentStep.saveApplication) {
      actions.quote.addStepToWizard(nextStep.key, false);
      handleNextStep();
      return;
    }

    if (nextStep.key === 'schemeChoice') {
      formValues.calculateAllSchemes = true;
    }

    if (isFinalStep) {
      set(formValues, 'card.cardNumber', encryptCardNumber);
      set(formValues, 'card.cvv', encryptCardCvv);
    }

    if (nextStep.key === 'quote') {
      if (formValues.insured.companyName !== '') {
        if (!formValues.enrichment || formValues.insured.companyName !== formValues.enrichment.companyName) {
          return actions.quote.enrichmentCheck(
            formValues.insured.address,
            formValues.insured.companyName,
            formValues.insured.metadata.companyRegistrationNumber
              ? formValues.insured.metadata.companyRegistrationNumber : '',
            formValues.vehicles,
            formValues.renewal,
          )
          .then((data) => {
            formValues.enrichment = data;
            actions.quote.submitForm(
              formValues, isFinalStep, false, currentStep.saveApplicationWithPatch,
            ).then(handleNextStep);
          });
        }
      }
    }

    return actions.quote.submitForm(formValues, isFinalStep, false, currentStep.saveApplicationWithPatch)
      .then(handleNextStep);
  }

  private handleNextStep = (formValues, nextStep: IStep) => () => {
    const { actions } = this.props;

    if (nextStep.key === 'complete') {
      return actions.quote.purchase(formValues)
        .then((data) => this.completePurchase(data, nextStep.route))
        .catch((error) => {
          if (this.handlePurchaseError(formValues, error)) {
            throw error;
          }
        });
    }

    actions.push(nextStep.route);
    this.scrollToTop();

    if (nextStep.calculatePremium) {
      actions.quote.calculate(formValues);
    }
  }

  private renderStepRoutes = (product: IProduct) => {
    const { steps, quote, i18n, channel } = this.props;

    return steps.map((step, key) => {
      const { route: routePath } = step;
      const exact = routePath === '/';

      const onBack = key > 0 ? this.handleBack : null;

      return (
        <Route key={key} exact={exact} path={routePath}>
          <div className={'animated fadeIn' + ' ' + step.key}>
            <step.component
              i18n={i18n}
              onBack={onBack}
              product={product}
              renewal={quote.application.renewal}
              channel={channel}
              onSubmit={this.handleSubmit}
              handleCalculate={this.handleCalculate}
              initialValues={quote.application}
              renderDefinition={this.renderDefinition(product)}
              {...this.props}
            />
          </div>
        </Route>
      );
    });
  }

  private renderDefinition = (product: IProduct) => (name: string) => {
    const definition = product.metadata.definitions[name];

    if (! definition) {
      return null;
    }

    const popover = (
      <Popover
        id={`popover-${name}`}
      >
        <Commonmark
          source={definition}
          linkTarget={'_blank'}
        />
      </Popover>
    );

    return (
      <OverlayTrigger
        trigger="click"
        rootClose={true}
        placement="right"
        positionTop={50}
        overlay={popover}
        positionLeft={200}
      >
        <button className="info-btn" type="button">
          <FaInfoCircle className="" />
        </button>
      </OverlayTrigger>
    );
  }

  private renderPaymentModal = () => {
    return (
      <BootstrapModal
        show={true}
        bsSize="large"
        keyboard={false}
        backdrop="static"
        onHide={this.closeModal}
      >
        <ModalHeader closeButton={true}>
          <ModalTitle>Payment</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <iframe src={api.getPaymentUrl(this.state.transactionId)} className="payments-modal" />
        </ModalBody>
      </BootstrapModal>
    );
  }

  private renderBreadcrumbs = () => {
    const { steps, quote } = this.props;

    const stepsFiltered = steps.filter((step) => {
      return true;
    });

    const referralsOrDeclines = getReferralsAndDeclines(quote);
    const currentLocation = this.getCurrentLocationFromSteps(stepsFiltered) || 0;
    let showLinks = stepsFiltered[currentLocation].key !== 'complete'
      && (referralsOrDeclines.empty || (! referralsOrDeclines.empty && ! stepsFiltered[currentLocation].showReferrals));
    let breadCrumbNum = 0;

    const items = stepsFiltered.map((step, index) => {
      let itemClass = 'visible';
      const isCurrentTab = index === currentLocation;
      if (index < currentLocation) {
        itemClass += ' previous';
      }
      if (isCurrentTab) {
        itemClass += ' active';
      }

      if (quote.application.status === QuoteStatus.REFERRAL_APPROVED) {
        showLinks = referralsOrDeclines.enableLinkOnTabs.includes(stepsFiltered[index].key);
      }
      breadCrumbNum++;

      return (
        <li key={index}>
          {showLinks ? (
            <Link
              className={itemClass}
              to={isCurrentTab ? '#' : step.route}
            >
              <div>{breadCrumbNum}</div>
              <span>{step.title}</span>
            </Link>
          ) : (
            <a href="javascript:void(0)" className={itemClass}>
              <div>{breadCrumbNum}</div>
              <span>{step.title}</span>
            </a>
          )}
        </li>
      );
    });

    return (
      <div className="breadcrumb-custom-container">
        <div className="container">
          <ul className="breadcrumb-custom">
            {items}
          </ul>
        </div>
      </div>
    );
  }

  public render() {
    const { actions, steps, quote, location } = this.props;
    const product = actions.branding.getProduct();
    const currentLocation = this.getCurrentLocation() || 0;

    if (quote.application.steps && quote.application.steps.indexOf(steps[currentLocation].key) === -1) {
      return (
        <Redirect to={steps[0].route} />
      );
    }

    if (location) {
      if (! quote.startQuoteProcess && location.pathname === '/') {
        actions.quote.startQuoteProcess();
      }
    }

    return (
      <div className={'internal-page'}>
        <AutoAffix viewportOffsetTop={0} offsetTop={15} container={this} affixStyle={{ zIndex: '999' }}>
            <div
              className="sub-header"
            >
              {quote.startQuoteProcess && this.renderBreadcrumbs()}
            </div>
        </AutoAffix>
        <div className="container">
          {quote.startQuoteProcess && (
            <Row>

              <Col xs={12} md={9} className="column">
                <div className="quote-content-container">
                  <Switch>
                    {this.renderStepRoutes(product)}
                  </Switch>
                </div>
              </Col>
              <Col xs={12} md={3} className="column hide-mobile-sidebar">
                <AutoAffix viewportOffsetTop={50} offsetTop={15} container={this}>
                  {/* This needs to have a div intermediary otherwise it doesn't work */}
                  <div>
                    <QuoteSidebar product={product} step={steps[currentLocation].key}/>
                  </div>
                </AutoAffix>
              </Col>
              <Col xs={12} md={3} className="column show-desktop-sidebar">
                <div>
                  <QuoteSidebar product={product} step={steps[currentLocation].key}/>
                </div>
              </Col>
            </Row>
          )}
        </div>

        {this.state.showModal && this.renderPaymentModal()}
      </div>
    );
  }
}

let selector;

export default flow([
  withJob({
    work: (props: IQuoteProps) => {
      const promises = [];

      return Promise.all(promises);
    },
    LoadingComponent: (props) => <Loading/>,
    ErrorComponent: ({ error }) => {
      console.error(error);

      return (
        <div>
          {error.message}
        </div>
      );
    },
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const encryptCardCvv = selector(state, 'card.cvv');
      const encryptCardNumber = selector(state, 'card.cardNumber');

      return {
        encryptCardCvv,
        auth: state.auth,
        encryptCardNumber,
        quote: state.quote,
        branding: state.branding,
        i18n: state.branding.i18n,
        isDrawerOpened: state.header.isDrawerOpened,
      };
    },
    (dispatch) => ({
      actions: {
        push: bindActionCreators(push, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
      },
    }),
  ),
  withRouter,
])(Quote);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/index.tsx